import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { FileText, RefreshCw, Download, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import DOMPurify from 'dompurify';

export default function ReportsView() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/enhance/reports');
        setReports(data.reports || []);
      } catch (e) { /* handled */ } finally { setLoading(false); }
    })();
  }, []);

  const generateReport = async (type) => {
    setGenerating(true);
    try {
      const { data } = await api.post('/api/enhance/reports/generate', { type, period_days: 30 });
      setReportData(data.report);
      setSelectedReport(data.report_id);
      setReports(prev => [{ id: data.report_id, type, created_at: new Date().toISOString(), generated_by_name: 'You' }, ...prev]);
      toast.success(`${type.toUpperCase()} report generated`);
    } catch { toast.error('Report generation failed'); }
    finally { setGenerating(false); }
  };

  const viewReport = async (reportId) => {
    try {
      const { data } = await api.get(`/api/enhance/reports/${reportId}`);
      setReportData(data.data || data);
      setSelectedReport(reportId);
    } catch { toast.error('Failed to load report'); }
  };

  const printReport = () => {
    const w = window.open('', '_blank');
    if (!w || !reportData) return;
    const s = (v) => DOMPurify.sanitize(String(v || ''));
    const html = `<!DOCTYPE html><html><head><title>${s(reportData.title || 'Report')}</title>
      <style>body{font-family:system-ui;max-width:800px;margin:40px auto;padding:20px;color:#111}
      h1{font-size:24px;border-bottom:2px solid #D4A843;padding-bottom:8px}h2{font-size:16px;margin-top:24px}
      table{width:100%;border-collapse:collapse;margin:16px 0}th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:13px}
      th{background:#f3f4f6}.badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700}
      .summary{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:16px;margin:16px 0}
      .stat{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:16px;text-align:center}
      .stat p{margin:0}.stat .val{font-size:28px;font-weight:700}.stat .label{font-size:11px;color:#6b7280}
      footer{margin-top:40px;font-size:11px;color:#9ca3af;text-align:center;border-top:1px solid #e5e7eb;padding-top:16px}</style></head><body>
      <h1>${s(reportData.title || 'Report')}</h1>
      <p style="color:#6b7280;font-size:13px">Period: ${s(reportData.period)} | Generated: ${s(new Date(reportData.generated_at).toLocaleString())} | By: ${s(reportData.generated_by)}</p>
      ${reportData.summary ? `<div class="summary">
        <div class="stat"><p class="val">${s(reportData.summary.total_tickets)}</p><p class="label">Total Tickets</p></div>
        <div class="stat"><p class="val">${s(reportData.summary.resolved)}</p><p class="label">Resolved</p></div>
        <div class="stat"><p class="val">${s(reportData.summary.open)}</p><p class="label">Open</p></div>
        <div class="stat"><p class="val">${s(reportData.summary.compliance_pct)}%</p><p class="label">SLA Compliance</p></div>
      </div>` : ''}
      ${reportData.tickets ? `<h2>Tickets (${reportData.tickets.length})</h2><table><thead><tr><th>ID</th><th>Title</th><th>Status</th><th>Priority</th><th>Created</th></tr></thead><tbody>
        ${reportData.tickets.map(t => `<tr><td style="font-family:monospace;font-size:11px">${s((t.id||'').slice(0,8))}</td><td>${s(t.title)}</td><td>${s(t.status)}</td><td>${s(t.priority)}</td><td>${s(new Date(t.created_at).toLocaleDateString())}</td></tr>`).join('')}
      </tbody></table>` : ''}
      ${reportData.total_events ? `<h2>Audit Events (${s(reportData.total_events)})</h2><p>Showing first ${(reportData.events||[]).length} events.</p>` : ''}
      <footer>${s(reportData.org_name || 'TechOps')} — EIGOO Inc | eigooinc.net</footer></body></html>`;
    w.document.write(html);
    w.document.close();
  };

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#D4A843]" /> Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">Generate branded compliance, SLA, and audit reports</p>
        </div>
      </div>

      {/* Generate Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { type: 'sla', label: 'SLA Performance Report', desc: 'Ticket resolution times, compliance rates, priority breakdown' },
          { type: 'compliance', label: 'Compliance Report', desc: 'Cannabis/HIPAA compliance status, license verification' },
          { type: 'audit', label: 'Audit Trail Report', desc: 'Activity logs, user actions, system changes' },
        ].map(r => (
          <div key={r.type} className="bg-white border border-gray-200 rounded-lg p-5 hover-lift" data-testid={`report-gen-${r.type}`}>
            <h3 className="text-sm font-bold text-gray-900 mb-1">{r.label}</h3>
            <p className="text-[10px] text-gray-500 mb-3">{r.desc}</p>
            <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => generateReport(r.type)} disabled={generating} data-testid={`report-btn-${r.type}`}>
              {generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileText className="w-3 h-3 mr-1" />} Generate
            </Button>
          </div>
        ))}
      </div>

      {/* Report Preview */}
      {reportData && (
        <div className="bg-white border border-[#D4A843]/20 rounded-lg p-6" data-testid="report-preview">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{reportData.title}</h3>
              <p className="text-xs text-gray-500">{reportData.period} | Generated: {new Date(reportData.generated_at).toLocaleString()}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={printReport} data-testid="report-print">
                <Download className="w-3 h-3 mr-1" /> Print / PDF
              </Button>
              <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => { setReportData(null); setSelectedReport(null); }} data-testid="report-close">Close</Button>
            </div>
          </div>
          {reportData.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {Object.entries(reportData.summary).map(([k, v]) => (
                <div key={k} className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-gray-900">{typeof v === 'number' && k.includes('pct') ? `${v}%` : v}</p>
                  <p className="text-[10px] text-gray-500 uppercase">{k.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          )}
          {reportData.tickets && (
            <div className="max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-gray-50"><th className="text-left px-3 py-2">Title</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Priority</th><th className="text-left px-3 py-2">Created</th></tr></thead>
                <tbody>{reportData.tickets.slice(0, 30).map(t => (
                  <tr key={t.id} className="border-b border-gray-100"><td className="px-3 py-2">{t.title}</td><td className="px-3 py-2">{t.status}</td><td className="px-3 py-2">{t.priority}</td><td className="px-3 py-2 text-gray-400">{new Date(t.created_at).toLocaleDateString()}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Report History */}
      {reports.length > 0 && !reportData && (
        <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="report-history">
          <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Previous Reports</h3>
          <div className="space-y-2">
            {reports.map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors" onClick={() => viewReport(r.id)} data-testid={`report-item-${r.id}`}>
                <div>
                  <p className="text-xs font-medium text-gray-900">{r.type?.toUpperCase()} Report</p>
                  <p className="text-[10px] text-gray-400">{new Date(r.created_at).toLocaleString()} — {r.generated_by_name}</p>
                </div>
                <FileText className="w-4 h-4 text-[#D4A843]" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
